import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { firmService, orderService, shippingService } from "../services/dataService";
import { Firm, ShipmentDetail, ShipmentLine, ShipmentStatus, ShipmentUpsert, ShippableOrder } from "../types";
import { usePermissions } from "../context/PermissionContext";
import { todayLocal } from "../utils/dates";
import PageLoader from "../components/PageLoader";
import ConfirmModal from "../components/ConfirmModal";
import ShipmentActionModal, { ShipmentAction } from "../components/ShipmentActionModal";
import FirmPickerField from "../components/FirmPicker";
import OrderPickerModal, { dueState } from "../components/OrderPicker";
import { SH_STATUS_LABEL, fmtDate, fmtDateTime, fmtQty } from "./Shipping";
import "../components/SendDocumentModal.css";
import "./Receivables.css";
import "./Purchasing.css";
import "./Shipping.css";

const today = () => todayLocal();
const parse = (s: string) => { const n = parseFloat((s || "").replace(",", ".")); return isNaN(n) ? 0 : n; };

const EPS = 0.0005;
/** Sunucu 3 basamağa yuvarlar; float gürültüsü girdiye sızmasın. */
const qtyText = (n: number) => String(Math.round(n * 1000) / 1000);

const QTY_PLAIN = /^\d+(?:[.,]\d{1,3})?$/;             // 1250 · 1250,5 · 1250.5
const QTY_TR = /^\d{1,3}(?:\.\d{3})+(?:,\d{1,3})?$/;   // 1.250 · 1.250,5
type QtyParse = { ok: true; value: number } | { ok: false; code: "empty" | "bad" | "zero" };
/** Miktar girdisi: sessizce kırpmaz, geçersizi reddeder (eski parse yalnız başlık alanlarında kaldı). */
const parseQty = (s: string): QtyParse => {
  const t = (s || "").trim();
  if (!t) return { ok: false, code: "empty" };
  let n: number;
  if (QTY_TR.test(t)) n = parseFloat(t.replace(/\./g, "").replace(",", "."));
  else if (QTY_PLAIN.test(t)) n = parseFloat(t.replace(",", "."));
  else return { ok: false, code: "bad" };
  if (!isFinite(n)) return { ok: false, code: "bad" };
  return n > EPS ? { ok: true, value: n } : { ok: false, code: "zero" };
};

const I = {
  plus: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" width="14" height="14"><path d="M12 5v14M5 12h14" /></svg>,
  x: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" width="14" height="14"><path d="M6 6l12 12M18 6 6 18" /></svg>,
};

interface Head {
  firmId: string; shipmentDate: string; carrier: string; vehiclePlate: string; driverName: string; driverTckn: string;
  trackingNo: string; packageCount: string; grossWeightKg: string; deliveryTitle: string; deliveryAddress: string;
  deliveryDistrict: string; deliveryCity: string; deliveryContact: string; deliveryPhone: string; waybillNo: string; waybillDate: string; note: string;
}
const emptyHead = (): Head => ({
  firmId: "", shipmentDate: today(), carrier: "", vehiclePlate: "", driverName: "", driverTckn: "", trackingNo: "", packageCount: "",
  grossWeightKg: "", deliveryTitle: "", deliveryAddress: "", deliveryDistrict: "", deliveryCity: "", deliveryContact: "", deliveryPhone: "",
  waybillNo: "", waybillDate: "", note: "",
});

/**
 * Sevkiyat formu: /shipping/new (taslak oluştur) ve /shipping/:id (detay; taslaksa düzenlenir).
 * Siparişler, "Sipariş ekle" seçicisiyle tek tek eklenir; kalan miktar aşılamaz.
 * Teslimat adresi firma kartından önerilir.
 */
const ShipmentForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { has, hasModule } = usePermissions();
  const isNew = !id;

  const [firms, setFirms] = useState<Firm[]>([]);
  const [detail, setDetail] = useState<ShipmentDetail | null>(null);
  const [head, setHead] = useState<Head>(emptyHead);
  const [orders, setOrders] = useState<ShippableOrder[]>([]);
  const [added, setAdded] = useState<string[]>([]);              // sevkiyata eklenen sipariş kimlikleri (satır sırası)
  // Eklenen siparişin son bilinen hâli: liste yeniden çekilince ya da çekilemeyince satır ekranda kalır
  const [snapOrders, setSnapOrders] = useState<Record<string, ShippableOrder>>({});
  const [touched, setTouched] = useState<Set<string>>(new Set()); // miktarı kullanıcı belirledi mi
  const [qty, setQty] = useState<Record<string, string>>({});
  const [pk, setPk] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(!isNew);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [ordersError, setOrdersError] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [badId, setBadId] = useState<string | null>(null);
  const [info, setInfo] = useState("");
  const [pickerOpen, setPickerOpen] = useState(false);
  const [undoSnap, setUndoSnap] = useState<{ added: string[]; qty: Record<string, string>; pk: Record<string, string>; touched: string[]; msg: string } | null>(null);
  const [pendingFirm, setPendingFirm] = useState<Firm | null | undefined>(undefined);
  const [action, setAction] = useState<ShipmentAction | null>(null);
  const [printHtml, setPrintHtml] = useState<string | null>(null);
  const [focusTarget, setFocusTarget] = useState<string | null>(null); // yeni satır çizildikten sonra miktara odaklan
  const seeded = useRef(false);
  const ordersGen = useRef(0);
  const detailRef = useRef<ShipmentDetail | null>(null);
  const [detailVersion, setDetailVersion] = useState(0); // her başarılı detay yüklemesinde artar → kalan tavanı tazelenir
  const justApplied = useRef(false); // Uygula sonrası odak yeni satıra gider, "Sipariş ekle" düğmesine değil
  const addBtnRef = useRef<HTMLButtonElement>(null);

  const editable = isNew || detail?.status === ShipmentStatus.Draft;

  useEffect(() => { firmService.getFirms().then(setFirms).catch(() => setFirms([])); }, []);

  const loadDetail = useCallback(async () => {
    if (!id) return;
    setLoading(true); setError("");
    try {
      const d = await shippingService.shipment(id);
      setDetail(d); detailRef.current = d; setDetailVersion((v) => v + 1);
      setHead({
        firmId: d.firmId, shipmentDate: d.shipmentDate.slice(0, 10), carrier: d.carrier || "", vehiclePlate: d.vehiclePlate || "",
        driverName: d.driverName || "", driverTckn: d.driverTckn || "", trackingNo: d.trackingNo || "",
        packageCount: d.packageCount?.toString() || "", grossWeightKg: d.grossWeightKg?.toString() || "",
        deliveryTitle: d.deliveryTitle || "", deliveryAddress: d.deliveryAddress || "", deliveryDistrict: d.deliveryDistrict || "",
        deliveryCity: d.deliveryCity || "", deliveryContact: d.deliveryContact || "", deliveryPhone: d.deliveryPhone || "",
        waybillNo: d.waybillNo || "", waybillDate: d.waybillDate ? d.waybillDate.slice(0, 10) : "", note: d.note || "",
      });
      // Kayıtlı satırlar forma eklenmiş sayılır; miktarları kullanıcı kararıdır ("otomatik" çipi almaz)
      setAdded(d.lines.map((l) => l.orderId));
      setTouched(new Set(d.lines.map((l) => l.orderId)));
      setQty(Object.fromEntries(d.lines.map((l) => [l.orderId, qtyText(l.quantity)])));
      setPk(Object.fromEntries(d.lines.map((l) => [l.orderId, l.packageCount?.toString() || ""])));
      setUndoSnap(null);
    } catch (e: any) { setError(e?.response?.data?.message || "Sevkiyat yüklenemedi."); }
    finally { setLoading(false); }
  }, [id]);

  useEffect(() => { loadDetail(); }, [loadDetail]);

  // Yeni kayıtta ?firmId=&orderId= ile ön seçim (Sipariş / Fatura Yönetimi ekranlarından gelir)
  useEffect(() => {
    if (!isNew) return;
    const f = params.get("firmId");
    if (f) { setHead((h) => ({ ...h, firmId: f })); return; }
    const ids = params.getAll("orderId");
    if (!ids.length) return;
    orderService.getById(ids[0])
      .then((o) => setHead((h) => ({ ...h, firmId: o.firmId || "" })))
      .catch(() => setError("Bağlantıdaki sipariş bulunamadı; firmayı seçip siparişi ekleyin."));
  }, [isNew, params]);

  /** Firmanın sevk edilebilir siparişleri; düzenlemede bu sevkiyatın kendi miktarı kalana geri eklenir. */
  const loadOrders = useCallback(async () => {
    if (!head.firmId || !editable) { setOrders([]); return; }
    const gen = ++ordersGen.current; // yavaş kalan eski firmanın yanıtı yeni listeyi ezmesin
    setOrdersLoading(true); setOrdersError("");
    try {
      let list = await shippingService.shippableOrders(head.firmId, !isNew);
      if (gen !== ordersGen.current) return;
      if (detailRef.current) {
        const own = new Map(detailRef.current.lines.map((l) => [l.orderId, l.quantity]));
        list = list.map((o) => (own.has(o.orderId)
          ? { ...o, remaining: o.remaining + (own.get(o.orderId) || 0), shipped: Math.max(0, o.shipped - (own.get(o.orderId) || 0)) } // bu taslağın kendi miktarı "sevk edilmiş" sayılmaz
          : o))
          .filter((o) => o.remaining > EPS || own.has(o.orderId));
      }
      setOrders(list);
      setSnapOrders((m) => { const n = { ...m }; list.forEach((o) => { if (n[o.orderId]) n[o.orderId] = o; }); return n; });
    } catch {
      if (gen !== ordersGen.current) return;
      setOrdersError("Siparişler yüklenemedi."); // eklenen satırlar ekranda kalır (anlık görüntüden)
    } finally { if (gen === ordersGen.current) setOrdersLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [head.firmId, editable, isNew, detailVersion]);

  useEffect(() => { loadOrders(); }, [loadOrders]);

  // Derin bağlantıdaki sipariş(ler) liste gelince BİR kez satır olarak eklenir; seçici açılmaz
  useEffect(() => {
    if (!isNew || seeded.current || !head.firmId || ordersLoading) return;
    const ids = Array.from(new Set(params.getAll("orderId")));
    if (!ids.length) { seeded.current = true; return; }
    seeded.current = true;
    const ok = ids.filter((x) => orders.some((o) => o.orderId === x));
    if (ok.length) {
      setAdded(ok);
      setQty(Object.fromEntries(ok.map((x) => [x, qtyText(orders.find((o) => o.orderId === x)!.remaining)])));
      setPk(Object.fromEntries(ok.map((x) => [x, ""])));
    }
    const miss = ids.length - ok.length;
    if (miss > 0) {
      setInfo(miss === 1 && ids.length === 1
        ? "Bağlantıdaki sipariş sevkiyata eklenemedi: iptal edilmiş, pasife alınmış ya da sevk edilecek kalanı yok."
        : `${miss} sipariş eklenemedi: sevk edilebilir değil ya da başka firmaya ait.`);
    }
  }, [isNew, orders, ordersLoading, head.firmId, params]);

  const firm = useMemo(() => firms.find((f) => f.firmId === head.firmId), [firms, head.firmId]);

  const applyFirmAddress = () => {
    if (!firm) return;
    setHead((h) => ({
      ...h,
      deliveryTitle: firm.firmName, deliveryAddress: firm.address || "",
      deliveryDistrict: firm.district?.districtName || "", deliveryCity: firm.city?.cityName || "",
      deliveryContact: firm.contactPerson || "", deliveryPhone: firm.phone || "",
    }));
  };

  const set = (k: keyof Head, v: string) => { setHead({ ...head, [k]: v }); setError(""); };

  // ---- Satırlar ----
  const lines = useMemo(
    () => added.map((x) => orders.find((o) => o.orderId === x) ?? snapOrders[x]).filter((o): o is ShippableOrder => !!o),
    [added, orders, snapOrders]);
  /** Kayıtlı ama listede olmayan satırlar (sipariş iptal/pasif) — yalnız düzenleme kipinde doğar. */
  const orphanLines = useMemo(
    () => (ordersLoading && orders.length === 0 ? [] : added).filter((x) => !orders.some((o) => o.orderId === x) && !snapOrders[x])
      .map((x) => detail?.lines.find((l) => l.orderId === x))
      .filter((l): l is ShipmentLine => !!l),
    [added, orders, snapOrders, detail, ordersLoading]);
  /** Birim başına toplam — farklı birimler birbirine EKLENMEZ. */
  const totals = useMemo(() => {
    const m = new Map<string, number>();
    lines.forEach((o) => { const p = parseQty(qty[o.orderId] ?? ""); if (p.ok) m.set(o.unit, (m.get(o.unit) ?? 0) + p.value); });
    return Array.from(m.entries()).map(([unit, q]) => `${fmtQty(q)} ${unit}`);
  }, [lines, qty]);
  const pkTotal = useMemo(() => lines.reduce((s, o) => s + (parseInt(pk[o.orderId] || "", 10) || 0), 0), [lines, pk]);
  const dirtyCount = useMemo(() => lines.filter((o) => (qty[o.orderId] ?? "") !== qtyText(o.remaining)).length, [lines, qty]);

  // Eklenen ilk yeni satırın miktar girdisine odaklan (satır çizildikten sonra)
  useEffect(() => {
    if (!focusTarget) return;
    const el = document.querySelector<HTMLInputElement>(`[data-qty="${focusTarget}"]`);
    if (el) { el.focus(); el.select(); setFocusTarget(null); }
  }, [focusTarget, lines]);

  const snap = (msg: string) => setUndoSnap({ added, qty, pk, touched: Array.from(touched), msg });
  /** Yalnız çıkarılan satırları geri getirir; kalan satırlarda sonradan yazılan miktarlar korunur. */
  const undo = () => {
    if (!undoSnap) return;
    setAdded(undoSnap.added);
    const back = undoSnap.added.filter((x) => !added.includes(x));
    setQty((m) => { const n = { ...m }; back.forEach((x) => { n[x] = undoSnap.qty[x] ?? ""; }); return n; });
    setPk((m) => { const n = { ...m }; back.forEach((x) => { n[x] = undoSnap.pk[x] ?? ""; }); return n; });
    setTouched((t) => { const n = new Set(t); back.forEach((x) => { if (undoSnap.touched.includes(x)) n.add(x); else n.delete(x); }); return n; });
    setUndoSnap(null); setError(""); setBadId(null);
  };

  const applyOrders = (ids: string[], picked: ShippableOrder[]) => {
    const removed = added.filter((x) => !ids.includes(x));
    if (removed.length) {
      const saved = removed.filter((x) => detail?.lines.some((l) => l.orderId === x)).length;
      snap(saved > 0
        ? `Kayıtlı ${saved} satır çıkarıldı; taslak kaydedilmeden kalıcı olmaz.`
        : `${removed.length} sipariş sevkiyattan çıkarıldı.`);
    } else setUndoSnap(null);

    setAdded(ids);
    setSnapOrders((m) => { const n = { ...m }; picked.forEach((o) => { n[o.orderId] = o; }); return n; });
    setQty((m) => { // yalnız EKSİK olanlar doldurulur: elle yazılmış miktar korunur
      const n = { ...m };
      picked.forEach((o) => { if (n[o.orderId] === undefined) n[o.orderId] = o.remaining > EPS ? qtyText(o.remaining) : ""; });
      return n;
    });
    setPk((m) => { const n = { ...m }; ids.forEach((x) => { if (n[x] === undefined) n[x] = ""; }); return n; });
    setError(""); setBadId(null);

    const firstNew = ids.find((x) => !added.includes(x)) ?? null;
    justApplied.current = !!firstNew; // yeni satır yoksa odak "Sipariş ekle" düğmesine dönsün
    setFocusTarget(firstNew);
  };

  const removeLine = (orderId: string, label: string) => {
    snap(detail?.lines.some((l) => l.orderId === orderId)
      ? "Kayıtlı 1 satır çıkarıldı; taslak kaydedilmeden kalıcı olmaz."
      : `${label} sevkiyattan çıkarıldı.`);
    setAdded((a) => a.filter((x) => x !== orderId));
    setError(""); setBadId(null);
    if (added.length === 1) window.setTimeout(() => addBtnRef.current?.focus(), 0);
  };

  const fillAllRemaining = () => {
    setQty((m) => { const n = { ...m }; lines.forEach((o) => { n[o.orderId] = qtyText(o.remaining); }); return n; });
    setTouched((t) => { const n = new Set(t); lines.forEach((o) => n.add(o.orderId)); return n; });
    setError(""); setBadId(null);
  };

  const setLineQty = (orderId: string, v: string) => {
    setQty((m) => ({ ...m, [orderId]: v }));
    setTouched((t) => new Set(t).add(orderId));
    setError(""); setBadId(null);
  };

  /** Enter / Shift+Enter: satırlar arasında miktar girdisinde gezinir. */
  const qtyKey = (e: React.KeyboardEvent<HTMLInputElement>, index: number) => {
    if (e.key !== "Enter") return;
    e.preventDefault();
    const next = lines[index + (e.shiftKey ? -1 : 1)];
    if (next) document.querySelector<HTMLInputElement>(`[data-qty="${next.orderId}"]`)?.focus();
  };

  const applyFirm = (f: Firm | null) => {
    set("firmId", f?.firmId || "");
    setAdded([]); setQty({}); setPk({}); setTouched(new Set()); setUndoSnap(null); setBadId(null); setSnapOrders({}); seeded.current = true;
  };

  const focusQty = (orderId: string) => {
    const el = document.querySelector<HTMLInputElement>(`[data-qty="${orderId}"]`);
    el?.focus(); el?.scrollIntoView({ block: "center" });
  };

  const buildDto = (): ShipmentUpsert | null => {
    if (!head.firmId) { setError("Firma seçin."); return null; }
    if (added.length === 0) { setError("Sevkiyata en az bir sipariş ekleyin."); return null; }
    const unknown = added.filter((x) => !lines.some((o) => o.orderId === x) && !orphanLines.some((l) => l.orderId === x));
    if (unknown.length > 0) { setError("Bazı satırların siparişi okunamadı; listeyi yenileyip tekrar deneyin."); return null; }
    if (orphanLines.length > 0) {
      setError(`${orphanLines[0].modelCode || "Sipariş"}: sipariş listede yok; satırı kaldırın.`);
      return null;
    }
    const out: { orderId: string; quantity: number; packageCount: number | null }[] = [];
    for (const o of lines) {
      const code = o.modelCode || "Sipariş";
      const p = parseQty(qty[o.orderId] ?? "");
      if (!p.ok) {
        setError(p.code === "empty" ? `${code}: sevk miktarı girin.`
          : p.code === "zero" ? `${code}: sevk miktarı sıfırdan büyük olmalı.`
            : `${code}: sevk miktarı geçersiz. Ondalık için virgül kullanın (örn. 12,5).`);
        setBadId(o.orderId); focusQty(o.orderId); return null;
      }
      if (p.value > o.remaining + EPS) {
        setError(`${code}: kalan ${fmtQty(o.remaining)} ${o.unit} aşılamaz.`);
        setBadId(o.orderId); focusQty(o.orderId); return null;
      }
      const pkRaw = (pk[o.orderId] || "").trim();
      if (pkRaw && !/^\d+$/.test(pkRaw)) {
        setError(`${code}: koli adedi tam sayı olmalı.`); setBadId(null);
        const el = document.querySelector<HTMLInputElement>(`[data-pk="${o.orderId}"]`);
        el?.focus(); el?.scrollIntoView({ block: "center" });
        return null;
      }
      out.push({ orderId: o.orderId, quantity: p.value, packageCount: pkRaw ? parseInt(pkRaw, 10) : null });
    }
    return {
      firmId: head.firmId, shipmentDate: head.shipmentDate,
      carrier: head.carrier || null, vehiclePlate: head.vehiclePlate || null, driverName: head.driverName || null,
      driverTckn: head.driverTckn || null, trackingNo: head.trackingNo || null,
      packageCount: head.packageCount ? parseInt(head.packageCount, 10) : null,
      grossWeightKg: head.grossWeightKg ? (parseQty(head.grossWeightKg).ok ? (parseQty(head.grossWeightKg) as { ok: true; value: number }).value : parse(head.grossWeightKg)) : null,
      deliveryTitle: head.deliveryTitle || null, deliveryAddress: head.deliveryAddress || null,
      deliveryDistrict: head.deliveryDistrict || null, deliveryCity: head.deliveryCity || null,
      deliveryContact: head.deliveryContact || null, deliveryPhone: head.deliveryPhone || null,
      waybillNo: head.waybillNo || null, waybillDate: head.waybillNo && head.waybillDate ? head.waybillDate : null,
      note: head.note || null,
      lines: out,
    };
  };

  const save = async (thenShip: boolean) => {
    const dto = buildDto();
    if (!dto) return;
    setSaving(true); setError(""); setInfo("");
    try {
      let sid = id;
      if (isNew) { const r = await shippingService.create(dto); sid = r.shipmentId; }
      else await shippingService.update(id!, dto);
      if (thenShip) {
        navigate(`/shipping/${sid}`, { replace: true });
        // Sevk modalı detay yüklendikten sonra açılır
        setTimeout(() => setAction("ship"), 0);
        return;
      }
      if (isNew) navigate(`/shipping/${sid}`, { replace: true });
      else { setInfo("Taslak kaydedildi."); await loadDetail(); }
    } catch (e: any) {
      const d = e?.response?.data; setError(typeof d === "string" ? d : d?.message || "Kaydedilemedi.");
    } finally { setSaving(false); }
  };

  const remove = async () => {
    if (!id || !window.confirm("Taslak sevkiyat silinsin mi?")) return;
    try { await shippingService.remove(id); navigate("/shipping"); }
    catch (e: any) { setError(e?.response?.data || "Silinemedi."); }
  };

  const openPrint = async () => {
    if (!id) return;
    try { setPrintHtml(await shippingService.printHtml(id)); }
    catch { setError("Yazdırma görünümü alınamadı."); }
  };
  const doPrint = () => {
    const frame = document.getElementById("sh-print") as HTMLIFrameElement | null;
    frame?.contentWindow?.focus(); frame?.contentWindow?.print();
  };

  if (loading) return <PageLoader message="Sevkiyat yükleniyor..." />;

  const st = detail?.status;
  const rowCount = lines.length + orphanLines.length;

  return (
    <div className="rc-page">
      <Link to="/shipping" className="rc-crumb">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" width="16" height="16"><path d="M15 6l-6 6 6 6" /></svg>
        Sevkiyat
      </Link>

      <div className="rc-card pu-head">
        <div>
          <h1 style={{ display: "flex", alignItems: "center", gap: "0.75rem", flexWrap: "wrap" }}>
            {isNew ? "Yeni Sevkiyat" : detail?.number}
            {st && <span className={`sh-status s${st}`}>{SH_STATUS_LABEL[st]}</span>}
          </h1>
          <div className="pu-head-meta">
            {isNew ? "Taslak olarak kaydedin ya da doğrudan sevk edin." : (
              <>Oluşturan <b>{detail?.createdBy || "—"}</b> · {fmtDate(detail?.createdAt)}
                {detail?.shippedAt && <> · Sevk {fmtDateTime(detail.shippedAt)}</>}
                {detail?.deliveredAt && <> · Teslim {fmtDateTime(detail.deliveredAt)} ({detail.receivedBy || "—"})</>}
                {detail?.cancelledAt && <> · İptal: {detail.cancelReason}</>}
              </>
            )}
          </div>
          <div className="rc-head-actions" style={{ marginTop: "0.9rem" }}>
            {editable && has(isNew ? "shipping:create" : "shipping:update") && (
              <>
                <button className="rc-btn primary" onClick={() => save(true)} disabled={saving}>Kaydet ve Sevk Et</button>
                <button className="rc-btn" onClick={() => save(false)} disabled={saving}>Taslak Kaydet</button>
              </>
            )}
            {!isNew && st === ShipmentStatus.Draft && has("shipping:update") && !saving && (
              <button className="rc-btn" onClick={() => setAction("ship")}>Sevk Et</button>
            )}
            {st === ShipmentStatus.Shipped && has("shipping:update") && (
              <button className="rc-btn primary" onClick={() => setAction("deliver")}>Teslim Edildi</button>
            )}
            {!isNew && <button className="rc-btn" onClick={openPrint}>İrsaliye Yazdır</button>}
            {(st === ShipmentStatus.Draft || st === ShipmentStatus.Shipped) && has("shipping:update") && !isNew && (
              <button className="rc-btn danger-ghost" onClick={() => setAction("cancel")}>İptal Et</button>
            )}
            {st === ShipmentStatus.Draft && has("shipping:delete") && (
              <button className="rc-btn danger-ghost" onClick={remove}>Sil</button>
            )}
          </div>
        </div>
        {detail && (
          <div className="pu-info">
            <div className="pu-info-item"><div className="pu-info-label">Toplam Miktar</div><div className="pu-info-value">{fmtQty(detail.totalQuantity)}</div></div>
            <div className="pu-info-item"><div className="pu-info-label">Koli</div><div className="pu-info-value">{detail.packageCount ?? "—"}</div></div>
            <div className="pu-info-item"><div className="pu-info-label">İrsaliye No</div><div className="pu-info-value mono">{detail.waybillNo || "—"}</div></div>
          </div>
        )}
      </div>

      {error && <div className="ic-alert error" style={{ marginTop: "1rem" }}>{String(error)}</div>}
      {info && <div className="ic-alert warn" style={{ marginTop: "1rem" }}>{info}</div>}

      {detail && st !== ShipmentStatus.Draft && (
        <div className="sh-timeline">
          <div className="sh-step done"><div className="lbl">Hazırlandı</div><div className="val">{fmtDate(detail.createdAt)}</div><div className="sub">{detail.createdBy || ""}</div></div>
          <div className={`sh-step ${st === ShipmentStatus.Cancelled ? "off" : "done"}`}><div className="lbl">Sevk Edildi</div><div className="val">{fmtDateTime(detail.shippedAt)}</div><div className="sub">{[detail.carrier, detail.vehiclePlate].filter(Boolean).join(" · ") || "—"}</div></div>
          <div className={`sh-step ${st === ShipmentStatus.Delivered ? "done" : st === ShipmentStatus.Shipped ? "cur" : "off"}`}><div className="lbl">Teslim</div><div className="val">{st === ShipmentStatus.Delivered ? fmtDateTime(detail.deliveredAt) : st === ShipmentStatus.Shipped ? "Yolda" : "—"}</div><div className="sub">{detail.receivedBy || ""}</div></div>
        </div>
      )}

      <div className="rc-card sh-doc" style={{ marginTop: "1rem" }}>
        <div className="sh-grid">
          <div className="pu-field half"><label>Firma</label>
            <FirmPickerField value={head.firmId} firms={firms} disabled={!editable} placeholder="Firma seçin"
              onChange={(f) => { const next = f?.firmId || ""; if (next === head.firmId) return; if (added.length > 0) setPendingFirm(f); else applyFirm(f); }} />
          </div>
          <div className="pu-field"><label>Sevk Tarihi</label><input type="date" className="ic-in" value={head.shipmentDate} disabled={!editable} onChange={(e) => set("shipmentDate", e.target.value)} /></div>
          <div className="pu-field"><label>Koli Sayısı</label><input className="ic-in num" inputMode="numeric" value={head.packageCount} disabled={!editable} onChange={(e) => set("packageCount", e.target.value)} /></div>
        </div>

        <div className="sh-section">
          <h3>Siparişler
            {editable && (
              <span className="sh-lines-bar">
                {rowCount > 0 && dirtyCount > 0 && <button type="button" className="rc-btn sm" onClick={fillAllRemaining}>Kalanları yaz</button>}
                <button ref={addBtnRef} type="button" className="ic-add" disabled={!head.firmId || ordersLoading || (!!ordersError && orders.length === 0)}
                  title={head.firmId ? undefined : "Önce firma seçin"} aria-haspopup="dialog" onClick={() => setPickerOpen(true)}>
                  {I.plus} Sipariş ekle
                </button>
              </span>
            )}
          </h3>

          {editable ? (
            <>
              {!head.firmId ? (
                <div className="rc-empty"><strong>Önce firma seçin</strong>Sevk edilecek siparişler firmaya göre listelenir.</div>
              ) : ordersLoading && orders.length === 0 ? (
                <div className="rc-hint">Siparişler yükleniyor…</div>
              ) : ordersError && rowCount === 0 ? (
                <div className="rc-empty"><strong>Siparişler yüklenemedi</strong><button type="button" className="rc-btn sm" onClick={loadOrders}>Yeniden dene</button></div>
              ) : rowCount === 0 ? (
                orders.length === 0 ? (
                  <div className="rc-empty"><strong>Sevk edilebilir sipariş yok</strong>Bu firmanın açık siparişi yok ya da tümü sevk edilmiş.</div>
                ) : (
                  <div className="rc-empty">
                    <strong>Henüz sipariş eklenmedi</strong>
                    {firm?.firmName ? `${firm.firmName} için ` : ""}{orders.length} sevk edilebilir sipariş var. Sevkiyata girecekleri "Sipariş ekle" ile seçin.
                    <button type="button" className="rc-btn primary sm" onClick={() => setPickerOpen(true)}>{I.plus} Sipariş ekle</button>
                  </div>
                )
              ) : (
                <div className="sh-orders sh-lines"><table>
                  <thead><tr><th>Model</th><th>Termin</th><th>Durum</th><th className="num">Sipariş</th><th className="num">Kalan</th><th className="num">Sevk Miktarı</th><th className="num">Koli</th><th className="act" /></tr></thead>
                  <tbody>
                    {lines.map((o, i) => {
                      const p = parseQty(qty[o.orderId] ?? "");
                      const over = p.ok && p.value > o.remaining + EPS;
                      const due = dueState(o.deadline);
                      const days = o.deadline ? Math.round((new Date(o.deadline).setHours(0, 0, 0, 0) - new Date().setHours(0, 0, 0, 0)) / 86400000) : null;
                      return (
                        <tr key={o.orderId} data-order-id={o.orderId} className={over || badId === o.orderId ? "invalid" : ""}>
                          <td className="c-model">
                            <b>{o.modelCode || "—"}</b>
                            {o.modelName && <div className="pu-stock-hint">{o.modelName}</div>}
                            {o.invoiceNumber && <div className="pu-stock-hint">Fatura {o.invoiceNumber}</div>}
                            {!touched.has(o.orderId) && <span className="rc-chip neutral sh-auto">otomatik</span>}
                          </td>
                          <td className="c-due" data-label="Termin">
                            {o.deadline ? (
                              <div className={`sh-due ${due}`}>{fmtDate(o.deadline)}
                                {days !== null && <small>{days < 0 ? `${-days} gün geç` : days === 0 ? "bugün" : `${days} gün kaldı`}</small>}
                              </div>
                            ) : "—"}
                          </td>
                          <td className="c-status" data-label="Durum"><span className={`rc-chip ${o.orderStatusId === 4 ? "ok" : "neutral"}`}>{o.orderStatusName || "—"}</span></td>
                          <td className="c-qty num" data-label="Sipariş">{fmtQty(o.quantity)} {o.unit}</td>
                          <td className="c-rem num" data-label="Kalan"><div className={`rem ${over ? "over" : o.remaining <= EPS ? "zero" : ""}`}>{fmtQty(o.remaining)}</div></td>
                          <td className="c-ship num" data-label="Sevk Miktarı">
                            <input className={`ic-in num ${over || badId === o.orderId ? "bad" : ""}`} inputMode="decimal" placeholder="0"
                              data-qty={o.orderId} value={qty[o.orderId] ?? ""} onFocus={(e) => e.currentTarget.select()}
                              onKeyDown={(e) => qtyKey(e, i)} onChange={(e) => setLineQty(o.orderId, e.target.value)} />
                          </td>
                          <td className="c-pk num" data-label="Koli">
                            <input className="ic-in pk" inputMode="numeric" data-pk={o.orderId} value={pk[o.orderId] ?? ""} onChange={(e) => setPk({ ...pk, [o.orderId]: e.target.value })} />
                          </td>
                          <td className="c-act act">
                            <button type="button" className="ic-rm" title="Satırı kaldır" aria-label={`${o.modelCode || "Sipariş"} satırını kaldır`} onClick={() => removeLine(o.orderId, o.modelCode || "Sipariş")}>{I.x}</button>
                          </td>
                        </tr>
                      );
                    })}
                    {orphanLines.map((l) => (
                      <tr key={l.shipmentLineId} className="invalid">
                        <td className="c-model"><b>{l.modelCode || "—"}</b>{l.modelName && <div className="pu-stock-hint">{l.modelName}</div>}<span className="rc-chip warn sh-auto">Sipariş listede yok</span></td>
                        <td className="c-due" data-label="Termin">—</td>
                        <td className="c-status" data-label="Durum">—</td>
                        <td className="c-qty num" data-label="Sipariş">—</td>
                        <td className="c-rem num" data-label="Kalan">—</td>
                        <td className="c-ship num" data-label="Sevk Miktarı"><b>{fmtQty(l.quantity)} {l.unit}</b></td>
                        <td className="c-pk num" data-label="Koli">{l.packageCount ?? "—"}</td>
                        <td className="c-act act"><button type="button" className="ic-rm" title="Satırı kaldır" aria-label="Satırı kaldır" onClick={() => removeLine(l.orderId, l.modelCode || "Sipariş")}>{I.x}</button></td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot><tr>
                    <td colSpan={5}>{rowCount} sipariş</td>
                    <td className="num">{totals.length ? `Toplam ${totals.join(" · ")}` : "—"}</td>
                    <td className="num">{pkTotal || "—"}</td>
                    <td />
                  </tr></tfoot>
                </table></div>
              )}
              {ordersError && rowCount > 0 && (
                <div className="sh-undo">Sipariş listesi yenilenemedi; satırlar son bilinen hâliyle gösteriliyor.
                  <button type="button" className="rc-btn sm" onClick={loadOrders}>Yeniden dene</button>
                </div>
              )}
              {undoSnap && (
                <div className="sh-undo" role="status">{undoSnap.msg}
                  <button type="button" className="rc-btn sm" onClick={undo}>Geri al</button>
                </div>
              )}
            </>
          ) : detail && detail.lines.length > 0 ? (
            <div className="sh-orders"><table>
              <thead><tr><th>Model</th><th>Sipariş Tarihi</th><th className="num">Sevk Miktarı</th><th className="num">Koli</th><th className="num">Sipariş Toplamı</th><th>Stok</th></tr></thead>
              <tbody>{detail.lines.map((l) => (
                <tr key={l.shipmentLineId}>
                  <td><b>{l.modelCode || "—"}</b>{l.modelName && <div className="pu-stock-hint">{l.modelName}</div>}</td>
                  <td>{fmtDate(l.orderDate)}</td>
                  <td className="num"><b>{fmtQty(l.quantity)} {l.unit}</b></td>
                  <td className="num">{l.packageCount ?? "—"}</td>
                  <td className="num">{fmtQty(l.orderShipped)} / {fmtQty(l.orderQuantity)}</td>
                  <td>{!l.hasStockMovement ? "—" : st === ShipmentStatus.Cancelled ? <span className="rc-chip warn">Geri alındı</span> : <span className="rc-chip ok">Düşüldü</span>}</td>
                </tr>
              ))}</tbody>
              <tfoot><tr>
                <td colSpan={2}>{detail.lines.length} sipariş</td>
                <td className="num">{Array.from(detail.lines.reduce((m, l) => m.set(l.unit, (m.get(l.unit) ?? 0) + l.quantity), new Map<string, number>()).entries()).map(([u, q]) => `${fmtQty(q)} ${u}`).join(" · ")}</td>
                <td className="num">{detail.lines.reduce((a, l) => a + (l.packageCount ?? 0), 0) || "—"}</td>
                <td /><td />
              </tr></tfoot>
            </table></div>
          ) : (
            <div className="rc-empty"><strong>Bu sevkiyatta satır yok</strong></div>
          )}
        </div>

        <div className="sh-section">
          <h3>Taşıma</h3>
          <div className="sh-grid">
            <div className="pu-field"><label>Nakliyeci / Kargo</label><input className="ic-in" value={head.carrier} maxLength={100} disabled={!editable} onChange={(e) => set("carrier", e.target.value)} /></div>
            <div className="pu-field"><label>Plaka</label><input className="ic-in" value={head.vehiclePlate} maxLength={20} placeholder="34ABC123" disabled={!editable} onChange={(e) => set("vehiclePlate", e.target.value.toUpperCase())} /></div>
            <div className="pu-field"><label>Şoför</label><input className="ic-in" value={head.driverName} maxLength={100} disabled={!editable} onChange={(e) => set("driverName", e.target.value)} /></div>
            <div className="pu-field"><label>Şoför TCKN</label><input className="ic-in" value={head.driverTckn} maxLength={11} inputMode="numeric" disabled={!editable} onChange={(e) => set("driverTckn", e.target.value.replace(/\D/g, ""))} /></div>
            <div className="pu-field"><label>Takip No</label><input className="ic-in" value={head.trackingNo} maxLength={50} disabled={!editable} onChange={(e) => set("trackingNo", e.target.value)} /></div>
            <div className="pu-field"><label>Brüt Ağırlık (kg)</label><input className="ic-in num" inputMode="decimal" value={head.grossWeightKg} disabled={!editable} onChange={(e) => set("grossWeightKg", e.target.value)} /></div>
            <div className="pu-field"><label>Resmi İrsaliye No</label><input className="ic-in" value={head.waybillNo} maxLength={32} disabled={!editable} placeholder="Sevk sırasında da girilebilir" onChange={(e) => set("waybillNo", e.target.value)} /></div>
            <div className="pu-field"><label>İrsaliye Tarihi</label><input type="date" className="ic-in" value={head.waybillDate} disabled={!editable || !head.waybillNo} onChange={(e) => set("waybillDate", e.target.value)} /></div>
          </div>
        </div>

        <div className="sh-section">
          <h3>Teslimat Adresi {editable && firm && <button type="button" className="rc-btn sm" onClick={applyFirmAddress} style={{ marginLeft: "auto", textTransform: "none", letterSpacing: 0 }}>Firma kartından al</button>}</h3>
          <div className="sh-grid">
            <div className="pu-field half"><label>Teslim Yeri / Unvan</label><input className="ic-in" value={head.deliveryTitle} maxLength={200} placeholder={firm?.firmName || ""} disabled={!editable} onChange={(e) => set("deliveryTitle", e.target.value)} /></div>
            <div className="pu-field"><label>İlgili Kişi</label><input className="ic-in" value={head.deliveryContact} maxLength={100} placeholder={firm?.contactPerson || ""} disabled={!editable} onChange={(e) => set("deliveryContact", e.target.value)} /></div>
            <div className="pu-field"><label>Telefon</label><input className="ic-in" value={head.deliveryPhone} maxLength={30} placeholder={firm?.phone || ""} disabled={!editable} onChange={(e) => set("deliveryPhone", e.target.value)} /></div>
            <div className="pu-field half"><label>Adres</label><input className="ic-in" value={head.deliveryAddress} maxLength={500} placeholder={firm?.address || "Boş bırakılırsa firma adresi kullanılır"} disabled={!editable} onChange={(e) => set("deliveryAddress", e.target.value)} /></div>
            <div className="pu-field"><label>İlçe</label><input className="ic-in" value={head.deliveryDistrict} maxLength={100} placeholder={firm?.district?.districtName || ""} disabled={!editable} onChange={(e) => set("deliveryDistrict", e.target.value)} /></div>
            <div className="pu-field"><label>İl</label><input className="ic-in" value={head.deliveryCity} maxLength={100} placeholder={firm?.city?.cityName || ""} disabled={!editable} onChange={(e) => set("deliveryCity", e.target.value)} /></div>
          </div>
        </div>

        <div className="sh-section">
          <h3>Not</h3>
          <textarea className="ic-in ic-textarea" rows={3} value={head.note} maxLength={500} disabled={!editable} placeholder="Teslimat koşulu, alıcıya not…" onChange={(e) => set("note", e.target.value)} />
        </div>

        {!hasModule("stock") && editable && (
          <div className="ic-total-hint" style={{ marginTop: "1rem" }}>Stok modülü lisansta olmadığı için sevk yalnız miktar takibi yapar; stok bakiyesi değişmez.</div>
        )}
      </div>

      {editable && (
        <OrderPickerModal
          open={pickerOpen}
          onClose={() => { setPickerOpen(false); const applied = justApplied.current; justApplied.current = false; if (!applied) window.setTimeout(() => addBtnRef.current?.focus(), 0); }}
          orders={orders}
          loading={ordersLoading}
          error={ordersError}
          onReload={loadOrders}
          value={added}
          onApply={applyOrders}
          firmName={firm?.firmName}
        />
      )}

      <ConfirmModal
        isOpen={pendingFirm !== undefined}
        title="Firma değiştirilsin mi?"
        message={`Sevkiyata eklenen ${added.length} sipariş bu firmaya ait. Firma değişirse satırlar kaldırılacak.`}
        type="danger"
        confirmText="Firmayı değiştir"
        cancelText="Vazgeç"
        onConfirm={() => { applyFirm(pendingFirm ?? null); setPendingFirm(undefined); }}
        onCancel={() => setPendingFirm(undefined)}
      />

      {action && detail && (
        <ShipmentActionModal shipment={detail} action={action} onClose={() => setAction(null)} onDone={async (m) => { setInfo(m); await loadDetail(); }} />
      )}

      {printHtml !== null && (
        <div className="ic-overlay rc-page" onClick={() => setPrintHtml(null)}>
          <div className="ic-panel" style={{ maxWidth: "60rem" }} onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
            <header className="ic-bar">
              <div className="ic-bar-left"><h2>Sevk İrsaliyesi</h2><span className="ic-chip neutral">{detail?.number}</span></div>
              <button className="ic-close" onClick={() => setPrintHtml(null)} aria-label="Kapat">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M6 6l12 12M18 6L6 18" /></svg>
              </button>
            </header>
            <div className="ic-body">
              <iframe id="sh-print" title="Sevk irsaliyesi" className="sh-print-frame" srcDoc={printHtml} />
            </div>
            <footer className="ic-actions">
              <span className="ic-actions-summary">A4 dikey · tarayıcı yazdırma penceresi açılır</span>
              <div className="ic-actions-btns">
                <button className="ic-btn" onClick={() => setPrintHtml(null)}>Kapat</button>
                <button className="ic-btn primary" onClick={doPrint}>Yazdır</button>
              </div>
            </footer>
          </div>
        </div>
      )}
    </div>
  );
};

export default ShipmentForm;
